import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import {
  getUserProfile,
  saveUserProfile,
  createDefaultProfile,
  isValidUsername,
  isUsernameAvailable,
  claimUsername
} from '../utils/socialFirestore';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { normalizeGenre, getNormalizedGenreOptions } from '../utils/normalize';
import myIcon from '../assets/logo512.png';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from '../components/AppSelect';

export default function ProfilePage() {
  const { user } = useAuth();
  const { inputStyle, textStyle } = useUI();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // For tune/set pickers
  const [allTunes, setAllTunes] = useState([]);
  const [allSets, setAllSets] = useState([]);
  const [tuneSearch, setTuneSearch] = useState('');
  const [setSearch, setSetSearch] = useState('');

  // Genres derived from user's tunes
  const [userGenres, setUserGenres] = useState([]);

  // Username
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(''); // '', 'checking', 'available', 'taken', 'invalid', 'yours'
  const [savedUsername, setSavedUsername] = useState(''); // the username that was last saved

  // Instruments as raw text for natural editing
  const [instrumentsText, setInstrumentsText] = useState('');

  const normalizeGenreList = (values = []) => getNormalizedGenreOptions(values);
  const normalizeGenreValue = (value = '') => normalizeGenre(value);
  const includesGenre = (values = [], value = '') => {
    const target = normalizeGenreValue(value);
    return values.some((item) => normalizeGenreValue(item) === target);
  };

  useEffect(() => {
    if (!user) return;
    loadProfile();
    loadTunesAndSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadProfile() {
    const existing = await getUserProfile(user.uid);
    if (existing) {
      setProfile({
        ...existing,
        genres: normalizeGenreList(existing.genres || []),
        publicTuneGenres: normalizeGenreList(existing.publicTuneGenres || []),
        publicSetGenres: normalizeGenreList(existing.publicSetGenres || []),
      });
      setInstrumentsText((existing.instruments || []).join(', '));
      setUsernameInput(existing.username || '');
      setSavedUsername(existing.username || '');
      if (existing.username) setUsernameStatus('yours');
    } else {
      setProfile(createDefaultProfile(user));
      setUsernameInput('');
    }
    setLoading(false);
  }

  async function loadTunesAndSets() {
    const tunesSnap = await getDocs(collection(db, 'users', user.uid, 'tunes'));
    const tunesData = tunesSnap.docs.map(d => ({
      id: d.id,
      title: d.data().title || 'Untitled',
      genre: normalizeGenreValue(d.data().genre || '')
    }));
    setAllTunes(tunesData);

    // Extract unique genres from tunes
    const genres = normalizeGenreList(tunesData.map(t => t.genre).filter(Boolean));
    setUserGenres(genres);

    // Build tune lookup for deriving set genres (sets don't have their own genre field)
    const tuneMap = {};
    tunesData.forEach(t => { tuneMap[t.id] = t; });

    const setsSnap = await getDocs(collection(db, 'users', user.uid, 'sets'));
    const setsData = setsSnap.docs.map(d => {
      const data = d.data();
      // Derive genres from component tunes
      const tuneGenres = (data.tunes || [])
        .map(t => tuneMap[t.id]?.genre)
        .filter(Boolean)
        .map(normalizeGenreValue);
      return {
        id: d.id,
        title: data.title || 'Untitled',
        genres: normalizeGenreList(tuneGenres),
      };
    });
    setAllSets(setsData);
  }

  function updateField(field, value) {
    setProfile(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  function toggleArrayItem(field, item) {
    if (field === 'genres' || field === 'publicTuneGenres' || field === 'publicSetGenres') {
      const normalizedItem = normalizeGenreValue(item);
      if (!normalizedItem) return;
      setProfile(prev => {
        const arr = normalizeGenreList(prev[field] || []);
        const alreadySelected = includesGenre(arr, normalizedItem);
        const next = alreadySelected
          ? arr.filter(i => normalizeGenreValue(i) !== normalizedItem)
          : [...arr, normalizedItem];
        return { ...prev, [field]: normalizeGenreList(next) };
      });
      setSaved(false);
      return;
    }
    setProfile(prev => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
      };
    });
    setSaved(false);
  }

  async function checkUsername(value) {
    if (!value) { setUsernameStatus(''); return; }
    if (!isValidUsername(value)) { setUsernameStatus('invalid'); return; }
    if (value.toLowerCase() === savedUsername.toLowerCase()) { setUsernameStatus('yours'); return; }
    setUsernameStatus('checking');
    const available = await isUsernameAvailable(value);
    setUsernameStatus(available ? 'available' : 'taken');
  }

  async function handleSave() {
    const newUsername = usernameInput.trim();

    // Validate username if provided
    if (newUsername && !isValidUsername(newUsername)) {
      alert('Username must be 3-20 characters, letters, numbers, hyphens, or underscores only.');
      return;
    }
    if (usernameStatus === 'taken') {
      alert('That username is already taken. Please choose another.');
      return;
    }

    setSaving(true);
    try {
      // Claim username if new or changed
      if (newUsername && newUsername.toLowerCase() !== savedUsername.toLowerCase()) {
        await claimUsername(user.uid, newUsername, savedUsername);
      }

      const instruments = instrumentsText.split(',').map(s => s.trim()).filter(Boolean);
      await saveUserProfile(user.uid, {
        ...profile,
        username: newUsername,
        instruments,
        genres: normalizeGenreList(profile.genres || []),
        publicTuneGenres: normalizeGenreList(profile.publicTuneGenres || []),
        publicSetGenres: normalizeGenreList(profile.publicSetGenres || []),
      });
      setSavedUsername(newUsername);
      if (newUsername) setUsernameStatus('yours');
      setSaved(true);
    } catch (err) {
      if (err.message === 'Username is already taken') {
        setUsernameStatus('taken');
        alert('That username was just taken. Please choose another.');
      } else {
        alert('Failed to save profile. Please try again.');
      }
    }
    setSaving(false);
  }

  if (loading) return <PageWrapperAuto><p style={{ textAlign: 'center' }}>Loading...</p></PageWrapperAuto>;
  if (!profile) return null;

  const filteredTunes = allTunes.filter(t =>
    t.title.toLowerCase().includes(tuneSearch.toLowerCase())
  );
  const filteredSets = allSets.filter(s =>
    s.title.toLowerCase().includes(setSearch.toLowerCase())
  );

  // Unique set genres (derived from component tunes)
  const setGenres = normalizeGenreList(allSets.flatMap(s => s.genres || []));
  const itemVisibilityOptions = [
    { value: 'none', label: 'None - hide all items' },
    { value: 'all', label: 'All - show all items' },
    { value: 'byGenre', label: 'By Category - share all items in specific categories' },
    { value: 'selected', label: 'Selected - choose individual items' },
  ];
  const setVisibilityOptions = [
    { value: 'none', label: 'None - hide all sets' },
    { value: 'all', label: 'All - show all sets' },
    { value: 'byGenre', label: 'By Category - share all sets in specific categories' },
    { value: 'selected', label: 'Selected - choose individual sets' },
  ];

  const chipStyle = (selected) => ({
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    border: selected
      ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 64%, var(--card-border) 36%)'
      : '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
    backgroundColor: selected
      ? 'color-mix(in srgb, var(--text-link, #228be6) 24%, var(--input-bg) 76%)'
      : 'color-mix(in srgb, var(--input-bg) 90%, transparent)',
    color: 'var(--text-primary)',
    display: 'inline-block'
  });

  return (
    <PageWrapperAuto>
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '1rem' }}>
        <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'flex-start' }}>
          <button className="btn-subtle search-control-btn" onClick={() => navigate('/social')}>
            ← Back
          </button>
        </div>
        <div className="app-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <img src={myIcon} alt="Logo" className="responsive-icon" />
          <span>My Profile</span>
          <FaQuestionCircle
            onClick={() => navigate('/help#your-profile')}
            style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
            title="Help & Tutorials"
          />
        </div>

        {/* Basic Info */}
        <label style={textStyle}>Display Name</label>
        <input
          className="search-input"
          style={{ ...inputStyle, width: '100%', marginBottom: '0.75rem' }}
          value={profile.displayName}
          onChange={e => updateField('displayName', e.target.value)}
        />

        <label style={textStyle}>Username</label>
        <input
          className="search-input"
          style={{ ...inputStyle, width: '100%', marginBottom: '2px' }}
          value={usernameInput}
          onChange={e => {
            const val = e.target.value.replace(/\s/g, '');
            setUsernameInput(val);
            setSaved(false);
            checkUsername(val);
          }}
          placeholder="e.g., fiddle_player_42"
        />
        <p style={{
          fontSize: '0.75rem',
          marginBottom: '0.75rem',
          marginTop: 0,
          color: usernameStatus === 'available' ? '#2a2' :
                 usernameStatus === 'taken' ? '#e44' :
                 usernameStatus === 'invalid' ? '#e44' :
                 usernameStatus === 'yours' ? '#2a2' :
                 'var(--text-primary)',
          opacity: usernameStatus ? 1 : 0.6
        }}>
          {usernameStatus === 'checking' && 'Checking...'}
          {usernameStatus === 'available' && 'Available!'}
          {usernameStatus === 'taken' && 'Already taken'}
          {usernameStatus === 'invalid' && '3-20 chars, letters/numbers/hyphens/underscores only'}
          {usernameStatus === 'yours' && 'Your current username'}
          {!usernameStatus && 'Others will use this to find you'}
        </p>

        <label style={textStyle}>Bio</label>
        <textarea
          className="search-input add-entry-textarea"
          style={{ ...inputStyle, width: '100%', minHeight: '60px', marginBottom: '0.75rem', resize: 'vertical' }}
          value={profile.bio}
          onChange={e => updateField('bio', e.target.value)}
          placeholder="Tell others about yourself..."
        />

        <label style={textStyle}>Location</label>
        <input
          className="search-input"
          style={{ ...inputStyle, width: '100%', marginBottom: '0.75rem' }}
          value={profile.location}
          onChange={e => updateField('location', e.target.value)}
          placeholder="e.g., Indianapolis, IN"
        />

        {/* Instruments */}
        <label style={textStyle}>Instruments</label>
        <input
          className="search-input"
          style={{ ...inputStyle, width: '100%', marginBottom: '0.75rem' }}
          value={instrumentsText}
          onChange={e => {
            setInstrumentsText(e.target.value);
            setSaved(false);
          }}
          onBlur={() => {
            const parsed = instrumentsText.split(',').map(s => s.trim()).filter(Boolean);
            updateField('instruments', parsed);
            setInstrumentsText(parsed.join(', '));
          }}
          placeholder="e.g., banjo, mandolin, fiddle, flute, etc."
        />

        {/* Visibility */}
        <hr style={{ margin: '1rem 0', borderColor: 'var(--card-border)' }} />
        <h4 style={textStyle}>Visibility</h4>

        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            className="btn-subtle search-control-btn detail-header-pill detail-header-pill-editing"
            aria-expanded={profile.visibility === 'public'}
            onClick={() => updateField('visibility', profile.visibility === 'public' ? 'private' : 'public')}
            style={{ fontSize: '1rem', fontWeight: 700, padding: '4px 16px' }}
          >
            <span className="detail-header-pill-caret">{profile.visibility === 'public' ? '▼' : '▶'}</span>
            Make my profile public: {profile.visibility === 'public' ? 'On' : 'Off'}
          </button>
          <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, margin: '6px 0 0', textAlign: 'center' }}>
            When public, others can see the items you select below via your shared link.
          </p>
        </div>

        {profile.visibility === 'public' && (
          <>
            {/* Item Visibility */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={textStyle}>Item Visibility</label>
              <AppSelect
                className="compact-action-select"
                value={profile.tuneVisibility}
                onChange={(nextValue) => {
                  updateField('tuneVisibility', nextValue);
                  if (nextValue !== 'byGenre') updateField('publicTuneGenres', []);
                }}
                options={itemVisibilityOptions}
                active={profile.tuneVisibility !== 'none'}
                style={{ ...inputStyle, width: '100%', marginTop: '4px' }}
              />
              {profile.tuneVisibility === 'all' && allTunes.length > 0 && (
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '4px' }}>
                  {allTunes.length} item(s) will be public
                </p>
              )}
            </div>

            {profile.tuneVisibility === 'byGenre' && (
              <div style={{ marginBottom: '1rem', padding: '8px', backgroundColor: 'var(--input-bg)', borderRadius: '8px' }}>
                <p style={{ ...textStyle, fontSize: '0.85rem', marginBottom: '6px' }}>
                  Select categories to make public:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {userGenres.map(genre => (
                    <span
                      key={genre}
                      onClick={() => toggleArrayItem('publicTuneGenres', genre)}
                      style={chipStyle(includesGenre(profile.publicTuneGenres || [], genre))}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '6px' }}>
                  {allTunes.filter(t => includesGenre(profile.publicTuneGenres || [], t.genre)).length} item(s) will be public
                </p>
              </div>
            )}

            {profile.tuneVisibility === 'selected' && (
              <div style={{ marginBottom: '1rem', padding: '8px', backgroundColor: 'var(--input-bg)', borderRadius: '8px' }}>
                <input
                  className="search-input"
                  style={{ ...inputStyle, width: '100%', marginBottom: '6px' }}
                  placeholder="Search items..."
                  value={tuneSearch}
                  onChange={e => setTuneSearch(e.target.value)}
                />
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {filteredTunes.map(tune => (
                    <label key={tune.id} style={{
                      ...textStyle,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '3px 0',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={(profile.publicTuneIds || []).includes(tune.id)}
                        onChange={() => toggleArrayItem('publicTuneIds', tune.id)}
                      />
                      {tune.title}
                      {tune.genre && <span style={{ opacity: 0.5, fontSize: '0.8em', marginLeft: '4px' }}>({tune.genre})</span>}
                    </label>
                  ))}
                </div>
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '4px' }}>
                  {(profile.publicTuneIds || []).length} item(s) selected
                </p>
              </div>
            )}

            {/* Set Visibility */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={textStyle}>Set Visibility</label>
              <AppSelect
                className="compact-action-select"
                value={profile.setVisibility}
                onChange={(nextValue) => {
                  updateField('setVisibility', nextValue);
                  if (nextValue !== 'byGenre') updateField('publicSetGenres', []);
                }}
                options={setVisibilityOptions}
                active={profile.setVisibility !== 'none'}
                style={{ ...inputStyle, width: '100%', marginTop: '4px' }}
              />
              {profile.setVisibility === 'all' && allSets.length > 0 && (
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '4px' }}>
                  {allSets.length} set(s) will be public
                </p>
              )}
            </div>

            {profile.setVisibility === 'byGenre' && (
              <div style={{ marginBottom: '1rem', padding: '8px', backgroundColor: 'var(--input-bg)', borderRadius: '8px' }}>
                <p style={{ ...textStyle, fontSize: '0.85rem', marginBottom: '6px' }}>
                  Select categories to make public:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(setGenres.length > 0 ? setGenres : userGenres).map(genre => (
                    <span
                      key={genre}
                      onClick={() => toggleArrayItem('publicSetGenres', genre)}
                      style={chipStyle(includesGenre(profile.publicSetGenres || [], genre))}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '6px' }}>
                  {allSets.filter(s => (s.genres || []).some(g => includesGenre(profile.publicSetGenres || [], g))).length} set(s) will be public
                </p>
              </div>
            )}

            {profile.setVisibility === 'selected' && (
              <div style={{ marginBottom: '1rem', padding: '8px', backgroundColor: 'var(--input-bg)', borderRadius: '8px' }}>
                <input
                  className="search-input"
                  style={{ ...inputStyle, width: '100%', marginBottom: '6px' }}
                  placeholder="Search sets..."
                  value={setSearch}
                  onChange={e => setSetSearch(e.target.value)}
                />
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {filteredSets.map(set => (
                    <label key={set.id} style={{
                      ...textStyle,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '3px 0',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={(profile.publicSetIds || []).includes(set.id)}
                        onChange={() => toggleArrayItem('publicSetIds', set.id)}
                      />
                      {set.title}
                    </label>
                  ))}
                </div>
                <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, marginTop: '4px' }}>
                  {(profile.publicSetIds || []).length} set(s) selected
                </p>
              </div>
            )}
          </>
        )}

        {/* Save Button */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            className="btn-subtle search-control-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '10px 30px' }}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Profile'}
          </button>
        </div>
      </div>
    </PageWrapperAuto>
  );
}
